// A monster dropped from the compendium (issue 1045, option C): the DM drags a result out of the compendium onto the
// board or into the turn tracker, or presses its Place, and picks the name the players will see. The drag carries
// `{ segment, slug, name }` under one custom type; the drop asks THIS origin to place it (`requestJson`, so
// `authFetch`) and the server does the rest — the token, the link to the entry and its numbers, the combatant.
// Nothing comes with a drop as a picture: the site has no art for a monster yet, so the token is the plain disc.
import { EMPTY_GRID, numberedNames, snapToCells, stepCell, templateAdjustFrom, type Grid, type MonsterNumbers, type Point, type TemplateAdjust } from '@dragons-table/vtt-shared';
import { requestJson } from './api';
import { boardBus } from './board/bus';

/** The drag's own type: nothing but a compendium result the DM may place carries it. */
export const ENTRY_DRAG_TYPE = 'application/x-tdt-entry';

export interface DraggedEntry { segment: string; slug: string; name: string }

const ADDRESS = /^[a-z0-9][a-z0-9._-]{0,199}$/i;
const isEntry = (v: unknown): v is DraggedEntry => {
  const o = v as Record<string, unknown> | null;
  return !!o && typeof o.segment === 'string' && ADDRESS.test(o.segment) && typeof o.slug === 'string' && ADDRESS.test(o.slug)
    && typeof o.name === 'string' && o.name.trim().length >= 1 && o.name.length <= 200;
};

/** Put a result on a drag: the custom type, and its name as plain text for anywhere else it lands. */
export function setEntryDrag(dt: DataTransfer, e: DraggedEntry): void {
  dt.setData(ENTRY_DRAG_TYPE, JSON.stringify({ segment: e.segment, slug: e.slug, name: e.name }));
  dt.setData('text/plain', e.name);
  dt.effectAllowed = 'copy';
}

/** Whether a drag carries a result (its data cannot be read until the drop; its types can). */
export const hasEntryDrag = (dt: DataTransfer | null): boolean => !!dt && Array.from(dt.types ?? []).includes(ENTRY_DRAG_TYPE);

/** The result a drop carries, or null for anything else (a file, text, a malformed or hostile payload). */
export function readEntryDrag(dt: DataTransfer | null): DraggedEntry | null {
  if (!hasEntryDrag(dt)) return null;
  try {
    const v: unknown = JSON.parse(dt!.getData(ENTRY_DRAG_TYPE));
    return isEntry(v) ? { segment: v.segment, slug: v.slug, name: v.name } : null;
  } catch {
    return null;
  }
}

/** "Creature N": the next number no "Creature" at the table has taken yet, by the one numbering rule. */
export const nextCreatureName = (names: string[]): string => numberedNames('Creature', names, 0).source;

/**
 * The board host's drop (the DM's alone): a dragged result over the board may be dropped; dropped, the point under the
 * hand in board pixels goes to whoever opens the dialog. Anything else dragged over the board is left alone.
 */
export function boardDropProps(mayDrop: boolean) {
  return {
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      if (!mayDrop || !hasEntryDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      if (!mayDrop) return;
      const entry = readEntryDrag(e.dataTransfer);
      if (!entry) return;
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      boardBus.entryDropped({ entry, at: boardBus.pointAt(e.clientX - r.left, e.clientY - r.top) });
    },
  };
}

/** What a template placed with a monster changed, and its prose, shown and never applied (issue 1054). */
export interface PlacedTemplate { slug: string; name: string; changes: string[]; notes: TemplateNote[] }
export interface TemplateNote { label: string; text: string }

export type PlaceAnswer = { kind: 'ok'; id: string; combatant?: string; template?: PlacedTemplate; name?: string } | { kind: 'refused'; reason: string } | { kind: 'error' };

export interface PlaceAsk { segment: string; slug: string; x: number; y: number; name: string; hidden: boolean; tracker: boolean; template?: string }

/** Place a result on the scene the DM is looking at. A refusal carries the server's own words. */
export async function placeEntry(campaignId: string, ask: PlaceAsk, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<PlaceAnswer> {
  const body = { ...ask, x: Math.round(ask.x), y: Math.round(ask.y), ...(sceneId ? { scene_id: sceneId } : {}) };
  const { status, body: got } = await requestJson(`/api/tables/${encodeURIComponent(campaignId)}/compendium/place`, { method: 'POST', body: JSON.stringify(body) }, fetchImpl);
  const b = (got ?? {}) as { id?: unknown; combatant?: unknown; reason?: unknown; name?: unknown };
  if ((status === 200 || status === 201) && typeof b.id === 'string') {
    const t = (got as { template?: unknown } | null)?.template as Record<string, unknown> | undefined;
    const template: PlacedTemplate | null = t && typeof t.slug === 'string' && typeof t.name === 'string'
      ? { slug: t.slug, name: t.name, changes: Array.isArray(t.changes) ? t.changes.filter((c): c is string => typeof c === 'string') : [], notes: notesFrom(t.notes) }
      : null;
    return { kind: 'ok', id: b.id, ...(typeof b.combatant === 'string' ? { combatant: b.combatant } : {}), ...(template ? { template } : {}), ...(typeof b.name === 'string' ? { name: b.name } : {}) };
  }
  if (typeof b.reason === 'string') return { kind: 'refused', reason: b.reason };
  if (status === 403) return { kind: 'refused', reason: "That isn't yours to do." };
  if (status === 400) return { kind: 'refused', reason: 'That name or place cannot be used.' };
  return { kind: 'error' };
}

/**
 * The families a token may carry, as their segments: where a compendium result may be placed; and `markers`, those placed
 * as a hidden marker (issue 1584, a trap: no turn order, no template). None on failure.
 */
export async function placeKinds(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<{ segments: string[]; markers: string[] }> {
  const { status, body } = await requestJson(`/api/tables/${encodeURIComponent(campaignId)}/compendium/placeable`, {}, fetchImpl);
  const b = body as { segments?: unknown; markers?: unknown } | null;
  const strs = (v: unknown): string[] => (status === 200 && Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []);
  return { segments: strs(b?.segments), markers: strs(b?.markers) };
}

/** The families a token may carry, as their segments: where a compendium result may be placed. None on failure. */
export async function placeableSegments(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<string[]> {
  return (await placeKinds(campaignId, fetchImpl)).segments;
}

/** A template the DM may apply (issue 1054): its numeric adjustments and its prose. */
export interface TemplateChoice { slug: string; name: string; adjust: TemplateAdjust; notes: TemplateNote[] }

const notesFrom = (v: unknown): TemplateNote[] => (Array.isArray(v) ? v : [])
  .filter((n): n is TemplateNote => !!n && typeof (n as TemplateNote).label === 'string' && typeof (n as TemplateNote).text === 'string')
  .map((n) => ({ label: n.label, text: n.text }));
const numOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const numOrText = (v: unknown): number | string | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' ? v : null);

/**
 * The table's system's templates whose names match `q` (the first ones when it is empty; none for a system with no
 * templates), and the monster's own numbers to preview them on. None, and no base, on failure.
 */
export async function templateChoices(campaignId: string, q: string, entry: { segment: string; slug: string }, fetchImpl: typeof fetch = fetch): Promise<{ templates: TemplateChoice[]; base: MonsterNumbers | null }> {
  const qs = new URLSearchParams({ q: q.trim(), segment: entry.segment, slug: entry.slug });
  const { status, body } = await requestJson(`/api/tables/${encodeURIComponent(campaignId)}/compendium/templates?${qs.toString()}`, {}, fetchImpl);
  const b = (body ?? {}) as { templates?: unknown; base?: unknown };
  if (status !== 200 || !Array.isArray(b.templates)) return { templates: [], base: null };
  const templates = b.templates.flatMap((t): TemplateChoice[] => {
    const o = t as Record<string, unknown> | null;
    if (!o || typeof o.slug !== 'string' || typeof o.name !== 'string') return [];
    return [{ slug: o.slug, name: o.name, adjust: templateAdjustFrom(o.adjust), notes: notesFrom(o.notes) }];
  });
  const m = b.base as Record<string, unknown> | null | undefined;
  const base: MonsterNumbers | null = m && typeof m === 'object'
    ? { hp: numOrNull(m.hp), ac: numOrNull(m.ac), hitDice: numOrText(m.hitDice), dex: numOrNull(m.dex), con: numOrNull(m.con), cr: numOrText(m.cr) }
    : null;
  return { templates, base };
}

/** Where a drop with no point lands: the board's centre of view, or near the top left before the board has settled. */
export const viewCentreOr = (fallback: Point): Point => boardBus.viewCentre() ?? fallback;

const ONE = { w: 1, h: 1 };

/**
 * A spot the board's menu chose for what comes next (qa-runs#1425): "Place from the Compendium here" for the next thing
 * placed, "Add a scene link here" for the next link. Taken once; on this screen only, never sent.
 */
let chosen: { for: 'place' | 'link'; at: Point } | null = null;
export const menuSpot = {
  set(purpose: 'place' | 'link', at: Point): void { chosen = { for: purpose, at: { x: at.x, y: at.y } }; },
  take(purpose: 'place' | 'link'): Point | null { if (chosen?.for !== purpose) return null; const at = chosen.at; chosen = null; return at; },
};

/**
 * Where a token placed with no drop point starts (qa-runs#1425): the scene's default token spot where this person is sent
 * one and it is on the map, else the middle of their view.
 */
export function startPoint(scene: { tokenSpot?: Point }, extent: { width: number; height: number }): Point {
  const s = scene.tokenSpot;
  if (s && s.x >= 0 && s.y >= 0 && s.x <= extent.width && s.y <= extent.height) return { x: s.x, y: s.y };
  return viewCentreOr({ x: EMPTY_GRID.squarePx * 2.5, y: EMPTY_GRID.squarePx * 2.5 });
}

/** The free square a token with no drop point takes: from `startPoint`, outwards to the first square nothing stands on. */
export function newTokenSpot(state: { grid: Grid; scene: { snap?: boolean; tokenSpot?: Point }; objects: Record<string, { x: number; y: number }> }, extent: { width: number; height: number }): Point {
  return placeSpot(state.grid, extent, startPoint(state.scene, extent), state.scene.snap !== false, Object.values(state.objects).map((o) => ({ x: o.x, y: o.y })), true);
}

/**
 * Where a placed token stands: the point snapped as a placed token is (when the scene snaps); with `seek`, the first
 * square near it that nothing stands on yet, ring by ring outwards — a drop on the tracker, or Place, has no point of
 * its own, and a second goblin should not land on the first.
 */
export function placeSpot(grid: Grid, extent: { width: number; height: number }, point: Point, snap: boolean, taken: Point[], seek: boolean): Point {
  if (!snap) return { x: point.x, y: point.y };
  const start = snapToCells(grid, extent, point, ONE).centre;
  if (!seek) return start;
  const busy = (p: Point) => taken.some((t) => Math.abs(t.x - p.x) < 1 && Math.abs(t.y - p.y) < 1);
  for (let r = 0; r <= 6; r += 1) {
    for (let dj = -r; dj <= r; dj += 1) {
      for (let di = -r; di <= r; di += 1) {
        if (Math.max(Math.abs(di), Math.abs(dj)) !== r) continue;
        const p = r === 0 ? start : stepCell(grid, extent, start, ONE, di, dj);
        if (!busy(p)) return p;
      }
    }
  }
  return start;
}
